import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';

const categoryImages: Record<string, string> = {
  'xiangchu-zhidao': 'https://readdy.ai/api/search-image?query=warm%20family%20embrace%20mother%20and%20adult%20son%20smiling%20in%20living%20room%20soft%20natural%20light%20cozy%20atmosphere%20beige%20and%20cream%20tones%20editorial%20photography&width=800&height=400&seq=cat-xiangchu&orientation=landscape',
  'nvxu-yuemu': 'https://readdy.ai/api/search-image?query=respectful%20conversation%20between%20two%20people%20in%20modern%20home%20setting%20warm%20lighting%20professional%20atmosphere%20beige%20and%20soft%20green%20tones%20editorial%20style&width=800&height=400&seq=cat-nvxu&orientation=landscape',
  'daiji-guanxi': 'https://readdy.ai/api/search-image?query=three%20generations%20of%20family%20sitting%20together%20in%20sunlit%20living%20room%20peaceful%20atmosphere%20grandmother%20mother%20and%20child%20warm%20beige%20and%20cream%20tones&width=800&height=400&seq=cat-daiji&orientation=landscape',
  'yuemu-poxi': 'https://readdy.ai/api/search-image?query=two%20women%20different%20ages%20enjoying%20tea%20together%20in%20bright%20modern%20kitchen%20harmonious%20atmosphere%20soft%20warm%20lighting%20editorial%20photography%20beige%20tones&width=800&height=400&seq=cat-poxi&orientation=landscape',
  'qinggan-bianjie': 'https://readdy.ai/api/search-image?query=peaceful%20person%20reading%20by%20window%20morning%20light%20minimalist%20interior%20calm%20atmosphere%20self%20reflection%20concept%20soft%20cream%20and%20sage%20green%20tones&width=800&height=400&seq=cat-bianjie&orientation=landscape',
  'goutong-jiqiao': 'https://readdy.ai/api/search-image?query=family%20group%20sitting%20in%20circle%20having%20meaningful%20conversation%20in%20bright%20living%20room%20active%20listening%20warm%20cream%20and%20soft%20terracotta%20tones%20editorial%20photography&width=800&height=400&seq=cat-goutong&orientation=landscape',
};

const articleCardImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=warm%20family%20gathering%20young%20mother%20in%20law%20smiling%20with%20son%20in%20law%20modern%20living%20room%20soft%20natural%20light%20editorial%20photography%20cozy%20atmosphere%20beige%20and%20cream%20tones&width=600&height=400&seq=catcard-1&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=two%20men%20having%20respectful%20conversation%20in%20bright%20modern%20apartment%20warm%20lighting%20professional%20but%20relaxed%20atmosphere%20beige%20and%20soft%20green%20tones&width=600&height=400&seq=catcard-2&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=three%20generations%20family%20sitting%20together%20in%20bright%20living%20room%20warm%20sunlight%20streaming%20through%20windows%20peaceful%20atmosphere%20soft%20beige%20and%20cream%20palette&width=600&height=400&seq=catcard-3&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=two%20women%20one%20older%20one%20younger%20having%20tea%20together%20in%20bright%20modern%20kitchen%20sunlight%20through%20window%20harmonious%20atmosphere%20soft%20warm%20tones&width=600&height=400&seq=catcard-4&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=person%20sitting%20peacefully%20by%20window%20journaling%20morning%20light%20warm%20atmosphere%20minimalist%20interior%20self%20care%20concept%20soft%20cream%20and%20sage%20green%20tones&width=600&height=400&seq=catcard-5&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=family%20sitting%20in%20circle%20having%20open%20conversation%20in%20bright%20living%20room%20sunlight%20streaming%20warm%20atmosphere%20active%20listening%20cream%20and%20terracotta%20tones&width=600&height=400&seq=catcard-6&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=elegant%20middle%20aged%20woman%20sitting%20by%20window%20thoughtful%20expression%20soft%20morning%20light%20modern%20apartment%20warm%20beige%20and%20cream%20tones%20editorial%20portrait&width=600&height=400&seq=catcard-7&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=festive%20family%20dinner%20table%20with%20warm%20candlelight%20multiple%20generations%20enjoying%20meal%20together%20cozy%20dining%20room%20holiday%20atmosphere%20soft%20warm%20lighting%20cream%20tones&width=600&height=400&seq=catcard-8&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'xiangchu-zhidao');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <SEOHead
        title={`${currentCategory.name}`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},年轻的岳母,家庭代际关系,岳母婆媳矛盾,家庭情感边界,和谐家庭沟通技巧`}
        canonicalPath={`/category/${activeSlug}`}
        ogType="website"
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <section className="relative w-full h-[300px] md:h-[360px] flex items-end overflow-hidden">
          <img
            src={categoryImages[activeSlug] || categoryImages['xiangchu-zhidao']}
            alt={currentCategory.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pb-8 md:pb-10">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-white">
              {currentCategory.name}
            </h1>
            <p className="mt-2 text-sm md:text-base text-white/80 max-w-xl">
              {currentCategory.description}
            </p>
          </div>
        </section>

        <section className="py-8 md:py-10 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '分类' },
                { label: currentCategory.name },
              ]}
            />

            <div className="mt-6 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={articleCardImages[article.id] || articleCardImages[1]}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span>·</span>
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime} 分钟</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}